import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { CityServicePage } from "@/components/CityServicePage";
import type { RoofingServiceContent } from "@/components/RoofingServicePage";
import { caCityLocal } from "@/data/caCityLocal";
import { roofingTiles, solarTiles } from "@/data/serviceTiles";
import { roofingServicePages } from "@/data/roofingServiceContent";
import { solarServicePages } from "@/data/solarServiceContent";
import { roofingSystemPages } from "@/data/roofingSystemContent";
import { solarRepairPages } from "@/data/solarRepairContent";

const allRoofing: Record<string, RoofingServiceContent> = { ...roofingServicePages, ...roofingSystemPages };
const allSolar: Record<string, RoofingServiceContent> = { ...solarServicePages, ...solarRepairPages, "solar-carports": roofingSystemPages["solar-carports"] };

function resolve(citySlug: string, serviceSlug: string) {
  const city = caCityLocal[citySlug];
  if (!city) return null;
  const roofing = roofingTiles.find((t) => t.slug === serviceSlug);
  const solar = solarTiles.find((t) => t.slug === serviceSlug);
  const tile = roofing ?? solar;
  if (!tile) return null;
  const category: "roofing" | "solar" = roofing ? "roofing" : "solar";
  const base = category === "roofing" ? allRoofing[serviceSlug] : allSolar[serviceSlug];
  if (!base) return null;
  return { city, tile, category, base };
}

export const Route = createFileRoute("/areas/california/$city_/$service")({
  loader: ({ params }) => {
    const found = resolve(params.city, params.service);
    if (!found) throw notFound();
    return {
      citySlug: found.city.slug,
      cityName: found.city.shortName,
      serviceSlug: params.service,
      serviceTitle: found.tile.title,
    };
  },
  head: ({ loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Page not found – McKay Roofing & Solar" }, { name: "robots", content: "noindex" }] };
    }
    const { cityName, citySlug, serviceSlug, serviceTitle } = loaderData;
    const title = `${serviceTitle} in ${cityName}, CA | McKay Roofing & Solar`;
    const description = `Local ${serviceTitle.toLowerCase()} in ${cityName}, California. Licensed, insured Southern California crew since 1981. Free estimates – call (858) 541-1097.`;
    const url = `https://mckay.steadyautogrowthdemo.com/areas/california/${citySlug}/${serviceSlug}`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { name: "keywords", content: `${serviceTitle.toLowerCase()} ${cityName}, ${serviceTitle.toLowerCase()} ${cityName} CA, roofing ${cityName}, solar ${cityName}, McKay Roofing & Solar` },
        { property: "og:title", content: `${serviceTitle} in ${cityName}, CA` },
        { property: "og:description", content: description },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            name: `${serviceTitle} in ${cityName}, CA`,
            serviceType: serviceTitle,
            description,
            url,
            provider: {
              "@type": "RoofingContractor",
              name: "McKay Roofing & Solar",
              telephone: "+1-858-541-1097",
              areaServed: "Southern California",
            },
            areaServed: { "@type": "City", name: cityName, address: { "@type": "PostalAddress", addressRegion: "CA", addressCountry: "US" } },
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-4 py-24 text-center">
      <h1 className="text-3xl font-extrabold">Page not found</h1>
      <p className="mt-3 text-muted-foreground">That city and service combination isn't one of ours.</p>
      <Link to="/areas/california" className="mt-6 inline-block rounded-md bg-accent px-5 py-3 text-sm font-bold text-accent-foreground">
        Browse California service areas
      </Link>
    </div>
  ),
  component: CityServiceRoute,
});

function CityServiceRoute() {
  const { citySlug, serviceSlug } = Route.useLoaderData();
  const found = resolve(citySlug, serviceSlug);
  if (!found) return null;
  return (
    <CityServicePage
      city={found.city}
      serviceSlug={serviceSlug}
      serviceTitle={found.tile.title}
      category={found.category}
      base={found.base}
    />
  );
}
